import { createJurisdictionHeaders } from '@/data';
import { BetChannel } from '@/feature/betslip-sbk/utils/bet-submission/types';
import {
    NonPracticeGameMode,
    PracticeGameMode,
    Stake,
    arcadeApiUrl,
} from '@/feature/lobby-arcade/hooks/use-arcade-games';
import { useIsNewArcadePlatform } from '@/hooks/use-is-new-arcade-platform';
import { useMutation } from '@tanstack/react-query';

// types provided by RU https://github.com/betrapp/skill-games-documentation/blob/main/api-container/api-ruc.typings.md
export type SessionResponse =
    | {
          session_id: string;
          stake: Stake;
          game_mode: NonPracticeGameMode;
          expiration: string;
          iframe_url: string;
          betGuid: string;
      }
    | {
          session_id: string;
          game_mode: PracticeGameMode;
          expiration: string;
          iframe_url: string;
          betGuid: string;
      };

type PlayGamePayload = {
    game_id: string;
    game_mode: string;
    stake: Stake;
    channel: BetChannel;
    customer: {
        person_id: string;
        partner_user_id: string;
        geotoken: string;
    };
};

type PlayGameResponse = {
    data: {
        success: boolean;
        session: SessionResponse;
    };
};

type CustomStartGameErrorCodes =
    | 'no_iframe_url_found'
    | 'game_parameters_missing'
    | 'terms_and_conditions_failed'
    | 'terms_and_conditions_not_accepted'
    | 'geotoken_missing';

type PamStartGameErrorCodes =
    | 'custom_single_wager_amount_per_line_is_above_customer_max_single_wager'
    | 'wager_limit_reached_month'
    | 'wager_limit_reached_week'
    | 'wager_limit_reached_day'
    | 'wager_limit_reached';

type StartGameErrorCodes = CustomStartGameErrorCodes | PamStartGameErrorCodes;

export class StartGameError extends Error {
    code: StartGameErrorCodes;

    constructor(error_code: StartGameErrorCodes) {
        super(error_code);
        this.name = 'StartGameError';
        this.code = error_code;
    }
}

const startSession = async (payload: PlayGamePayload, isNewPlatform: boolean): Promise<PlayGameResponse> => {
    const response = await fetch(arcadeApiUrl('/auth/play-game', isNewPlatform), {
        method: 'POST',
        headers: {
            ...createJurisdictionHeaders(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
        if (data?.error_codes?.length) {
            throw new StartGameError(data.error_codes[0]);
        }
        throw data;
    }

    return data;
};

export const useStartGame = () => {
    const isNewPlatform = useIsNewArcadePlatform();

    return useMutation({
        mutationFn: (payload: PlayGamePayload) => startSession(payload, isNewPlatform),
    });
};
